import React, { useEffect, useMemo, useCallback } from 'react';
import { Card, Select, Table, Row, Col, Spin, message } from 'antd';
import { summaryColumns, assignmentColumns, timeSeriesConfig, checkDate } from '../utils/helper';
import { usePolicyDetail, usePolicyInterval } from '../utils/hooks';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getUserProfile } from '@src/store/common/selectors';
import DatePickerWithMenu from '../components/datepicker';
import KebabMenu from '../components/kebab';
import { NoGridTableWrapper } from '../utils/styles';
import { Footer } from '@src/components/common';
import BaseChart from '@src/components/common/BaseChart';
import moment from 'moment';
import PopOverMenu from '../components/popover';
import {
  DETAIL_TITLE,
  DETAIL_CONTENT,
  REQUEST_TITLE,
  REQUEST_CONTENT,
  ASSIGNMENT_TITLE,
  ASSIGNMENT_CONTENT,
} from '../utils/const';
import { DATE_SHORTCUTS } from '@utils/constants';

const PolicyDetails = () => {
  const {
    interval,
    comparisonInterval,
    localInterval,
    validateUpdateInterval,
    summaryDatePickerRef,
    comparisonDatePickerRef,
  } = usePolicyInterval();

  const [searchParams, setSearchParams] = useSearchParams();
  const policyId = searchParams.get('policy_id');
  const start_day = searchParams.get('start_day');
  const end_day = searchParams.get('end_day');
  const userProfile = useSelector(getUserProfile);
  const [isInitialized, setIsInitialized] = React.useState(false);

  useEffect(() => {
    if (!checkDate(start_day, end_day)) return;
    if (isInitialized) return;

    const interval: [moment.Moment, moment.Moment] =
      start_day && end_day
        ? [moment(start_day), moment(end_day)]
        : [localInterval[0], localInterval[1]];

    const shortcut = start_day && end_day ? 'custom' : DATE_SHORTCUTS.today;
    validateUpdateInterval(interval, shortcut);
    setIsInitialized(true);
  }, [start_day, end_day, validateUpdateInterval, isInitialized, localInterval]);

  const {
    data: detailData,
    isLoading: isLoading,
    isError: isError,
  } = usePolicyDetail({
    policy_id: policyId ? Number(policyId) : 0,
    start_day: start_day ?? localInterval[0].format('YYYY-MM-DD'),
    end_day: end_day ?? localInterval[1].format('YYYY-MM-DD'),
  });

  const handlePolicyChange = (value: number) => {
    searchParams.set('policy_id', String(value));
    setSearchParams(searchParams);
  };

  const handleDateChange = (dates: [moment.Moment, moment.Moment], short) => {
    if (!dates || dates.length < 2) return;

    if (dates[0].isAfter(dates[1])) {
      message.error('Start date must be earlier than or equal to end date');
      return;
    }

    searchParams.set('start_day', dates[0].format('YYYY-MM-DDTHH:mm:ss'));
    searchParams.set('end_day', dates[1].format('YYYY-MM-DDTHH:mm:ss'));
    setSearchParams(searchParams);
    validateUpdateInterval(dates, short);
  };

  const timeSeriesData = useMemo(() => {
    return detailData?.policy_curve || [];
  }, [detailData?.policy_curve]);

  const chartInstanceRefCb = useCallback(
    (instance) => {
      instance.setOption(timeSeriesConfig(timeSeriesData, userProfile.timezone));
    },
    [timeSeriesData, userProfile.timezone],
  );

  if (isLoading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin tip="Loading data..." />
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p style={{ color: 'red' }}>Failed to load data. Please try again later.</p>
        </div>
      </Card>
    );
  }

  const policyWithFilter = detailData?.policy_detail || [];
  const assignmentData = detailData?.cdn_weight || [];
  const summaryData = detailData?.policy_list || [];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8 }}>
            <span>Policy</span>
            {summaryData?.length > 0 && (
              <Select
                value={Number(policyId)}
                showSearch
                placeholder="Time"
                style={{ width: 280 }}
                options={summaryData.map((item) => ({
                  label: item.name,
                  value: item.policy_id,
                }))}
                onChange={handlePolicyChange}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            )}
          </Col>
          <Col>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isInitialized ? (
                <DatePickerWithMenu
                  interval={interval}
                  comparisonInterval={comparisonInterval}
                  validateUpdateInterval={handleDateChange}
                  summaryDatePickerRef={summaryDatePickerRef}
                  comparisonDatePickerRef={comparisonDatePickerRef}
                />
              ) : (
                <Spin />
              )}

              <KebabMenu
                detailData={detailData}
                policyId={Number(policyId)}
                profile={userProfile}
              />
            </div>
          </Col>
        </Row>

        <Card style={{ borderRadius: 8, marginBottom: 24 }}>
          <PopOverMenu summary={DETAIL_TITLE} content={DETAIL_CONTENT} />
          <NoGridTableWrapper>
            <Table
              columns={summaryColumns}
              dataSource={policyWithFilter}
              pagination={false}
              bordered={false}
              size="small"
              rowClassName={() => 'no-border-row'}
              style={{ marginRight: 20 }}
            />
          </NoGridTableWrapper>
        </Card>

        <Card title="Request Time Series" style={{ borderRadius: 8, marginBottom: 24 }}>
          <PopOverMenu summary={REQUEST_TITLE} content={REQUEST_CONTENT} />
          {timeSeriesData.length > 0 ? (
            <BaseChart
              chartId="policy-tracking-line-chart"
              chartInstanceRefCb={chartInstanceRefCb}
              style={{ height: 200 }}
            />
          ) : (
            <div>No Data</div>
          )}
        </Card>

        <Card title="Request Assignment" style={{ borderRadius: 8 }}>
          <PopOverMenu summary={ASSIGNMENT_TITLE} content={ASSIGNMENT_CONTENT} />
          <NoGridTableWrapper>
            <Table
              columns={assignmentColumns}
              dataSource={assignmentData}
              pagination={false}
              bordered={false}
              size="small"
              rowClassName={() => 'no-border-row'}
              style={{ marginRight: 20 }}
            />
          </NoGridTableWrapper>
        </Card>
        <Footer />
      </Card>
    </div>
  );
};

export default PolicyDetails;
